import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Container, CircularProgress, Alert } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faSearch, faRefresh, faPrint } from '@fortawesome/free-solid-svg-icons';
import useUnprintedChallans from '../hooks/useUnprintedChallans';
import Pagination from '../components/common/Pagination';
import { UI_COLORS } from '../utils/colors.jsx';
import { useTranslation } from 'react-i18next';

const AGING_META = {
    today:  { label: '< 1d',    bg: '#dcfce7', text: '#15803d' },
    '1d':   { label: '1-2d',    bg: '#fef9c3', text: '#854d0e' },
    '2-7d': { label: '2-7d',    bg: '#ffedd5', text: '#9a3412' },
    over_7d:{ label: '7d+',     bg: '#fee2e2', text: '#991b1b' },
};

const AgingBadge = ({ bucket }) => {
    const m = AGING_META[bucket] || { label: bucket, bg: '#f3f4f6', text: '#374151' };
    return (
        <span className="px-2 py-0.5 rounded-full text-xs font-bold"
            style={{ backgroundColor: m.bg, color: m.text }}>
            {m.label}
        </span>
    );
};

const PAGE_LIMIT = 50;

const UnprintedChallansPage = () => {
    const { t } = useTranslation();
    const { items, summary, total, loading, marking, error, fetchUnprinted, markPrinted } = useUnprintedChallans();

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(total / PAGE_LIMIT) || 1;

    const filterRef = useRef({ fromDate, toDate, search });
    filterRef.current = { fromDate, toDate, search };

    const buildParams = useCallback((page = 1) => {
        const { fromDate: fd, toDate: td, search: s } = filterRef.current;
        const p = { skip: (page - 1) * PAGE_LIMIT, limit: PAGE_LIMIT };
        if (fd) p.from_date = fd;
        if (td) p.to_date = td;
        if (s.trim()) p.search = s.trim();
        return p;
    }, []);

    useEffect(() => {
        fetchUnprinted(buildParams(1));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentPage > 1) fetchUnprinted(buildParams(currentPage));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleApply = () => {
        setCurrentPage(1);
        fetchUnprinted(buildParams(1));
    };

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={4}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box sx={{ bgcolor: '#0f172a', borderRadius: 2, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        <FontAwesomeIcon icon={faFileAlt} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">{t('common.analyticsWidgets.unprintedChallans.title')}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {t('common.analyticsWidgets.unprintedChallans.subtitle')}
                </Typography>
            </Box>

            {/* Summary tiles */}
            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    {[
                        { label: t('common.analyticsWidgets.unprintedChallans.tiles.totalUnprinted'), value: summary.total, color: '#0f172a' },
                        { label: t('common.analyticsWidgets.unprintedChallans.tiles.over1Day'), value: summary.over_1_day ?? 0, color: '#92400e' },
                        { label: t('common.analyticsWidgets.unprintedChallans.tiles.over7Days'), value: summary.over_7_days ?? 0, color: '#991b1b' },
                        { label: t('common.analyticsWidgets.unprintedChallans.tiles.oldestPending'), value: summary.oldest_pending_at ? (() => {
                            const diff = Date.now() - new Date(summary.oldest_pending_at).getTime();
                            const d = Math.floor(diff / 86400000);
                            const h = Math.floor((diff % 86400000) / 3600000);
                            return d > 0 ? `${d}d ${h}h ago` : `${h}h ago`;
                        })() : '—', color: '#6b7280' },
                    ].map(({ label, value, color }) => (
                        <div key={label} className="p-4 rounded-xl border bg-white" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</p>
                            <p className="text-2xl font-black" style={{ color }}>{value}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3 mb-4 p-4 rounded-xl border bg-white" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('common.analyticsColumns.fromDate')}</label>
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }} />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('common.analyticsColumns.toDate')}</label>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }} />
                </div>
                <div className="flex-1 w-full min-w-0 sm:min-w-[200px]">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('common.analyticsColumns.search')}</label>
                    <div className="relative">
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder={t('common.analyticsWidgets.unprintedChallans.searchPlaceholder')}
                            className="w-full border rounded-lg px-3 py-2 text-sm pr-8"
                            style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                            onKeyDown={e => { if (e.key === 'Enter') handleApply(); }}
                        />
                        <FontAwesomeIcon icon={faSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    </div>
                </div>
                <button onClick={handleApply} disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                    style={{ backgroundColor: '#0f172a' }}>
                    <FontAwesomeIcon icon={loading ? faRefresh : faSearch} className={loading ? 'animate-spin' : ''} />
                    {loading ? t('common.analyticsWidgets.unprintedChallans.loading') : t('common.analyticsWidgets.unprintedChallans.apply')}
                </button>
            </div>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>}

            {/* Table */}
            {loading && !items.length ? (
                <Box display="flex" justifyContent="center" py={10}>
                    <CircularProgress size={36} />
                </Box>
            ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <FontAwesomeIcon icon={faFileAlt} className="text-4xl mb-3 opacity-30" />
                    <p className="text-sm font-medium">{t('common.analyticsWidgets.unprintedChallans.empty')}</p>
                </div>
            ) : (
                <>
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="text-xs font-bold uppercase tracking-wider text-gray-500"
                                    style={{ backgroundColor: '#f9fafb', borderBottom: `2px solid ${UI_COLORS.BORDERS.LIGHT}` }}>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsColumns.challanNo')}</th>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsWidgets.unprintedChallans.order')}</th>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsColumns.customer')}</th>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsColumns.warehouse')}</th>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsColumns.challanDate')}</th>
                                    <th className="px-4 py-3 text-left">{t('common.analyticsColumns.generatedBy')}</th>
                                    <th className="px-4 py-3 text-center">{t('common.analyticsColumns.pending')}</th>
                                    <th className="px-4 py-3 text-center">{t('common.analyticsWidgets.unprintedChallans.age')}</th>
                                    <th className="px-4 py-3 text-center">{t('common.analyticsColumns.action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row, idx) => (
                                    <tr key={row.sub_order_id}
                                        style={{ borderTop: `1px solid ${UI_COLORS.BORDERS.LIGHT}`, backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <td className="px-4 py-3 font-mono font-semibold text-gray-900">{row.challan_number}</td>
                                        <td className="px-4 py-3">
                                            <p className="font-semibold text-gray-900">{row.sub_order_number}</p>
                                            <p className="text-xs text-gray-500">{row.order_number}</p>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">{row.customer_name}</td>
                                        <td className="px-4 py-3 text-gray-600">{row.warehouse_name ?? '—'}</td>
                                        <td className="px-4 py-3 text-gray-700">{row.challan_date}</td>
                                        <td className="px-4 py-3 text-gray-600">{row.challan_generated_by_name ?? '—'}</td>
                                        <td className="px-4 py-3 text-center font-semibold text-gray-800">{row.pending_human}</td>
                                        <td className="px-4 py-3 text-center"><AgingBadge bucket={row.aging_bucket} /></td>
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                onClick={() => markPrinted(row.sub_order_id)}
                                                disabled={marking === row.sub_order_id}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity disabled:opacity-60"
                                                style={{ backgroundColor: '#0f172a' }}
                                            >
                                                {marking === row.sub_order_id
                                                    ? <FontAwesomeIcon icon={faRefresh} className="animate-spin" />
                                                    : <FontAwesomeIcon icon={faPrint} />
                                                }
                                                Mark Printed
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={total}
                        onPageChange={setCurrentPage}
                        loading={loading}
                        totalLabel="unprinted challans"
                        pageSize={PAGE_LIMIT}
                    />
                </>
            )}
        </Container>
    );
};

export default UnprintedChallansPage;
